import React, { useState, useEffect } from 'react';
import { Image, Table, message } from 'antd';
import { useParams } from 'react-router-dom';
import { IProject } from 'utils/types/storyTypes';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { handleMessage } from 'utils/helper/message';
import ActionButton from 'components/table/ActionButton';
import {
  AddSquare,
  ArrowDown2,
  ArrowUp2,
  Edit2,
  ReceiveSquare,
  Trash,
} from 'iconsax-react';
import DeleteModal from 'components/modal/DeleteModal';
import isDownload from 'hooks/isDownload';
import EmotionGroupCreateModal from './EmotionGroupCreateModal';
import EmoticonGroupEditModal from './EmoticonGroupEditModal';
import EmoticonAssetUploadModal from './EmoticonAssetUploadModal';
import { emoticonApis } from 'apis/resource/emoticonApis';
import NoImage from 'components/etc/NoImage';
import EmoticonAssetEditModal from './EmoticonAssetEditModal';
import SearchInput from 'components/search/SearchInput';

/**
 * * 리소스 - 배경 목록
 */

interface IProps {
  selectProject: IProject;
}

const EmoticonTable = ({ selectProject }: IProps) => {
  const { t } = useTranslation();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string>(''); // 검색어
  const [visible, setVisible] = useState({
    createGroup: false,
    editGroup: false,
    createEmoticon: false,
    editEmoticon: false,
    delete: false,
    name: '',
  }); // 모달 show on/off
  const [deleteType, setDeleteType] = useState<string>('group');
  const [selectedData, setSelectedData] = useState<any>(null);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const res = await emoticonApis.emoticonList(projectId);
        setData(res.list);
      } catch (err) {
        console.log('fetch data error: ', err);
      }
    }
  };

  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // 배경 삭제
  const handleClickDeleteModal = (record, type) => {
    setDeleteType(type);
    setSelectedData(record);
    setVisible({
      ...visible,
      delete: true,
      name: record?.speaker,
    });
  };
  const handleDelete = async () => {
    if (selectedData) {
      try {
        setLoading(true);
        const deleteApi =
          deleteType === 'group'
            ? emoticonApis.emoticonDeleteGroup(projectId, selectedData.id)
            : emoticonApis.emoticonDeleteAsset(
                projectId,
                selectedData.emoticon_id,
                selectedData.id,
              );
        await deleteApi;
        setRefresh(!refresh);
        setLoading(false);
        setVisible({
          ...visible,
          delete: false,
          name: selectedData?.live_name,
        });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  /// 다운로드
  const handleDownload = (record) => {
    // 다운로드 함수로 보내준다.
    isDownload(record);
  };

  const expandedRowRender = (record) => {
    return (
      <MainTable
        columns={columnsNested}
        dataSource={record.slaves}
        isPagination={false}
      />
    );
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <AddSquare
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, createEmoticon: true });
            }}
          />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setSelectedData(record);
              setVisible({ ...visible, editGroup: true });
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 3,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record, 'group');
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const tableActionNestMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <ReceiveSquare size='24' onClick={() => handleDownload(record)} />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setVisible({ ...visible, editEmoticon: true });
              setSelectedData(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 3,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record, 'asset');
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 'fit-content',
      align: 'center',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.table.name',
      ),
      dataIndex: 'speaker',
      width: '9rem',
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} position='end' />;
      },
    },
    Table.EXPAND_COLUMN,
  ];

  const columnsNested = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '1rem',
      align: 'center',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.nestedTable.emoticon',
      ),
      dataIndex: 'image_url',
      width: '10rem',
      align: 'center',
      render: (_, record) => (
        <IDColumnWrapper>
          {_ === null || _ === '' ? (
            <NoImage />
          ) : (
            <Image
              src={_}
              alt={record.image_name}
              height={80}
              width={80}
              style={{ objectFit: 'cover' }}
            />
          )}
        </IDColumnWrapper>
      ),
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.nestedTable.assetsName',
      ),
      dataIndex: 'image_name',
      width: '10rem',
      align: 'left',
    },
    {
      dataIndex: 'move',
      width: '30rem',
      render: (text, record) => {
        const menus = [tableActionNestMenu(record)];
        return <ActionButton menus={menus} position='end' />;
      },
    },
  ];

  // handle search input
  useEffect(() => {
    if (searchData && searchData.length) {
      const cloneData = JSON.parse(JSON.stringify(data));
      const filterData = cloneData.filter((e) =>
        e.speaker.toLowerCase().includes(searchData.toLowerCase()),
      );
      setTableData(filterData);
    } else {
      setTableData(data);
    }
  }, [searchData]);

  return (
    <>
      <TableToolbar>
        <SearchInput placeholder={'Search...'} setSearchData={setSearchData} />
        <div>
          <DefaultButton
            name='register'
            onClick={() => setVisible({ ...visible, createGroup: true })}
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.header.create',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <TableWrapper>
        <MainTable
          rowKey='id'
          rowClassName={(record) => {
            if (record && record.id) {
              if (expandedRows.includes(record.id)) {
                return 'active';
              }
            }
          }}
          columns={columnsOrigin}
          dataSource={tableData}
          expandable={{
            expandedRowRender: (record) => {
              return expandedRowRender(record);
            },
            expandIcon: ({ expanded, onExpand, record }) => {
              // Customize the expand column icon based on the 'expanded' state
              return expanded ? (
                <ArrowUp2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <ArrowDown2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            },
            onExpandedRowsChange: (expandedRows) => {
              setExpandedRows(expandedRows);
            },
          }}
        />
      </TableWrapper>

      <EmotionGroupCreateModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        projectId={projectId}
      />

      <EmoticonGroupEditModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        data={selectedData}
        setData={setSelectedData}
        projectId={projectId}
      />

      <EmoticonAssetUploadModal
        visible={visible}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        projectId={projectId}
        data={selectedData}
      />

      <EmoticonAssetEditModal
        visible={visible.editEmoticon}
        setVisible={setVisible}
        setRefresh={setRefresh}
        refresh={refresh}
        projectId={projectId}
        data={selectedData}
        setData={setSelectedData}
      />

      <DeleteModal
        visible={visible.delete}
        setVisible={(e) => {
          setVisible({ ...visible, delete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.deleteEmoticonModal.title',
        )}
        content='storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.emoticonContent.deleteEmoticonModal.content'
        label={
          selectedData?.image_name
            ? selectedData?.image_name
            : selectedData?.speaker
        }
        handleDelete={handleDelete}
        loading={loading}
      />
    </>
  );
};

export default EmoticonTable;

const TableWrapper = styled.div`
  .ant-table table tr {
    vertical-align: top;
  }
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;

const IconArrowWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 5px;
  p {
    font-size: ${theme.fontSizes.normal} !important;
    font-weight: ${theme.fontWeights.bold} !important;
    line-height: 24px !important;
    letter-spacing: 0.2px !important;
    color: ${theme.colors.blackContent} !important;
  }
`;

const IDColumnWrapper = styled.div`
  display: flex;
  column-gap: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
